import React, { useEffect, useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import loginUser from "../api/loginUser";
import cxbUserForgotPassword from "../api/cxbUserForgotPassword";
import { useAppContext } from "../context/AppContext";

import Input from "./Input";
import InputCodeForm from "./InputCodeForm";
import Spinner from "./Spinner";

type LoginCardProps = {
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  onSubmitHandler: () => Promise<void>;
  loading: boolean;
  message: string;
  loginPassword: boolean;

  isCXBUser: boolean;
};

const LoginCard = ({
  email,
  setEmail,
  password,
  setPassword,
  onSubmitHandler,
  loading,
  message,
  loginPassword,

  isCXBUser,
}: LoginCardProps) => {
  const { setToken } = useAppContext();

  const [code, setCode] = useState(["", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const passwordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem("iris-accessToken");
    if (storedToken) {
      try {
        setToken(storedToken);
      } catch (err) {
        console.error("Invalid token:", err);
      }
    }
  }, [setToken]);

  useEffect(() => {
    if (loginPassword && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [loginPassword]);

  const loginMutation = useMutation({
    mutationFn: loginUser,
    onSuccess: () => {
      setResponseMessage("");
      setEmail("");
      setPassword("");
    },
    onError: (error: any) => {
      setResponseMessage?.(error?.message || "Login failed");
    },
  });

  const handleLogin = async () => {
    if (isCXBUser) return;

    try {
      setIsLoading(true);
      if (loginPassword) {
        if (!email || !password) {
          setResponseMessage("Please enter both email and password");
          return;
        }
        const data = await loginMutation.mutateAsync({ email, password });
        if (data?.data?.token) {
          setToken(data.data.token);
        }
      } else {
        await onSubmitHandler();
      }
    } catch (error: any) {
      console.error(error);
      setResponseMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!email || !newPassword || !confirmNewPassword) {
      setResponseMessage("Please fill in all password fields.");
      return;
    }
    if (newPassword.length < 8) {
      setResponseMessage("New password must be at least 8 characters.");
      return;
    }
    if (newPassword === "password") {
      setResponseMessage(
        "New password must be different from the default password.",
      );
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setResponseMessage("New passwords do not match.");
      return;
    }

    try {
      setIsLoading(true);
      setResponseMessage("");

      await cxbUserForgotPassword({
        email,
        password: newPassword,
        confirmPassword: confirmNewPassword,
      });

      // After successful password update, log the user in with the new password
      const data = await loginMutation.mutateAsync({
        email,
        password: newPassword,
      });
      if (data?.data?.token) {
        setToken(data.data.token);
      }
    } catch (error: any) {
      setResponseMessage(error?.message || "Failed to update password.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isCXBUser) {
      handleUpdatePassword();
    } else {
      handleLogin();
    }
  };

  return (
    <React.Fragment>
      {responseMessage === "Your account has not been activated yet." ? (
        <div className="card">
          <InputCodeForm code={code} setCode={setCode} email={email} />
        </div>
      ) : (
        <div className="card">
          <h2 className="title">Welcome to iRis.</h2>
          <p className="subtitle">The Amazon Connect partner in your pocket.</p>
          <form
            method="POST"
            onSubmit={handleSubmit}
            className="form-login-container"
            noValidate
          >
            <Input
              type="email"
              placeholder=" "
              className="input"
              label="Email Address"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || isCXBUser}
            />

            {loginPassword && (
              <div style={{ marginTop: "20px" }}>
                <Input
                  type="password"
                  placeholder=" "
                  className="input"
                  label="Password"
                  name="password"
                  value={password}
                  ref={passwordRef}
                  onChange={(e) => setPassword(e.target.value)}
                  maxLength={100}
                  disabled={isLoading}
                />
              </div>
            )}

            {isCXBUser && (
              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Input
                  type="password"
                  placeholder=" "
                  className="input"
                  label="New Password"
                  name="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  maxLength={100}
                  disabled={isLoading}
                />
                <Input
                  type="password"
                  placeholder=" "
                  className="input"
                  label="Confirm New Password"
                  name="confirm-new-password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  maxLength={100}
                  disabled={isLoading}
                />
              </div>
            )}

            <button
              type="submit"
              style={{ marginTop: "20px" }}
              className="button"
              disabled={loading || isLoading}
            >
              {loading || isLoading ? (
                <Spinner variant="small-spinner" alignSpin="center-spinner" />
              ) : (
                "Continue"
              )}
            </button>
          </form>

          {message && <p className="status-message">{message}</p>}
          {responseMessage && (
            <p className="status-message">{responseMessage}</p>
          )}
        </div>
      )}
      <p className="powered-by-blk">
        Powered BY:<span>iCXeed</span>
      </p>
    </React.Fragment>
  );
};

export default LoginCard;
